import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { MoreVert as MoreVertIcon } from '@mui/icons-material'
import { socialApi, type SocialActionResponse, type SocialTarget } from '@/api/social'
import {
  SocialTargetCreateDialog,
  SocialTargetEditDialog,
  SocialTokenDialog,
} from '@/components/admin/social/SocialTargetDialogs'
import { targetLabel } from '@/components/admin/social/socialUi'
import { formatDateDMY, formatDateTimeDMY } from '@/utils/date'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'

type ConfirmKind = 'disable' | 'revoke'

/** Вкладка «Каналы»: цели публикаций, их токены, проверка и включение */
export function SocialTargetsTab() {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [targets, setTargets] = useState<SocialTarget[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [menuTarget, setMenuTarget] = useState<SocialTarget | null>(null)

  const [createOpen, setCreateOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<SocialTarget | null>(null)
  const [tokenOpen, setTokenOpen] = useState(false)
  const [tokenTarget, setTokenTarget] = useState<SocialTarget | null>(null)

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmKind, setConfirmKind] = useState<ConfirmKind>('disable')
  const [confirmTarget, setConfirmTarget] = useState<SocialTarget | null>(null)
  const [confirming, setConfirming] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setTargets(await socialApi.getTargets())
    } catch (err) {
      setError(displayErrorMessage(getErrorMessage(err), t('admin.social.loadFailed')))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    void load()
  }, [load])

  const replaceTarget = (target: SocialTarget) => {
    setTargets((prev) => prev.map((item) => (item.id === target.id ? target : item)))
  }

  const runAction = async (
    target: SocialTarget,
    action: () => Promise<SocialActionResponse<SocialTarget>>,
    onDone: (response: SocialActionResponse<SocialTarget>) => void = (response) => setSuccess(response.message),
  ) => {
    setError(null)
    setSuccess(null)
    setBusyId(target.id)
    try {
      const response = await action()
      replaceTarget(response.data)
      onDone(response)
    } catch (err) {
      setError(displayErrorMessage(getErrorMessage(err), t('admin.social.actionFailed')))
    } finally {
      setBusyId(null)
    }
  }

  const closeMenu = () => setMenuAnchor(null)

  const openConfirm = (kind: ConfirmKind, target: SocialTarget) => {
    setConfirmKind(kind)
    setConfirmTarget(target)
    setConfirmOpen(true)
  }

  const handleConfirm = async () => {
    if (!confirmTarget) return

    const target = confirmTarget
    setConfirming(true)
    await runAction(target, () =>
      confirmKind === 'disable'
        ? socialApi.updateTarget(target.id, { enabled: false })
        : socialApi.revokeCredentials(target.id),
    )
    setConfirming(false)
    setConfirmOpen(false)
  }

  const handleTest = (target: SocialTarget) =>
    runAction(
      target,
      () => socialApi.testTarget(target.id),
      // Итог проверки всегда приходит 200: успех или подсказка по коду ошибки
      (response) => (response.data.last_check_ok ? setSuccess(response.message) : setError(response.message)),
    )

  const stateChip = (target: SocialTarget) => (
    <Chip
      size="small"
      color={target.enabled ? 'success' : 'default'}
      label={t(target.enabled ? 'admin.social.channels.enabled' : 'admin.social.channels.disabled')}
    />
  )

  const tokenCell = (target: SocialTarget) =>
    target.has_credentials && target.credentials_mask ? (
      <Box component="span" sx={{ fontFamily: 'monospace' }}>
        {target.credentials_mask}
      </Box>
    ) : (
      <Typography component="span" variant="body2" color="text.secondary">
        {t('admin.social.channels.tokenMissing')}
      </Typography>
    )

  const lastCheckCell = (target: SocialTarget) => {
    if (!target.last_check_at) {
      return (
        <Typography component="span" variant="body2" color="text.secondary">
          {t('admin.social.channels.neverChecked')}
        </Typography>
      )
    }

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <Typography component="span" variant="body2" sx={{ whiteSpace: 'nowrap' }}>
          {formatDateTimeDMY(target.last_check_at)}
        </Typography>
        <Chip
          size="small"
          color={target.last_check_ok ? 'success' : 'error'}
          label={t(target.last_check_ok ? 'admin.social.channels.checkOk' : 'admin.social.channels.checkFailed')}
        />
        {!target.last_check_ok && target.last_check_error && (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {target.last_check_error}
          </Typography>
        )}
      </Box>
    )
  }

  const titleCell = (target: SocialTarget) => (
    <>
      <Typography fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
        {target.title ?? '—'}
      </Typography>
      {target.title_checked_at && (
        <Typography variant="caption" color="text.secondary">
          {t('admin.social.channels.titleCheckedAt', { date: formatDateDMY(target.title_checked_at) })}
        </Typography>
      )}
    </>
  )

  const actionsButton = (target: SocialTarget) => (
    <IconButton
      size="small"
      aria-label={t('admin.users.actions')}
      disabled={busyId === target.id}
      onClick={(event) => {
        setMenuAnchor(event.currentTarget)
        setMenuTarget(target)
      }}
    >
      {busyId === target.id ? <CircularProgress size={16} /> : <MoreVertIcon fontSize="small" />}
    </IconButton>
  )

  const address = (target: SocialTarget) => target.chat_id ?? (target.group_id !== null ? String(target.group_id) : '—')

  const tableHead = (
    <TableHead>
      <TableRow>
        <TableCell>{t('admin.social.channels.columnTitle')}</TableCell>
        <TableCell>{t('admin.social.channels.columnPlatform')}</TableCell>
        <TableCell>{t('admin.social.channels.columnKey')}</TableCell>
        <TableCell>{t('admin.social.channels.columnAddress')}</TableCell>
        <TableCell>{t('admin.social.channels.columnToken')}</TableCell>
        <TableCell>{t('admin.social.channels.columnLastCheck')}</TableCell>
        <TableCell>{t('admin.social.channels.columnState')}</TableCell>
        <TableCell align="right">{t('admin.users.actions')}</TableCell>
      </TableRow>
    </TableHead>
  )

  return (
    <Box>
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Box sx={{ ml: isMobile ? 0 : 'auto', display: 'flex', gap: 1 }}>
          <Button variant="contained" size="small" sx={{ height: 40 }} onClick={() => setCreateOpen(true)}>
            {t('admin.social.channels.add')}
          </Button>
        </Box>
      </Box>

      {loading ? (
        isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[1, 2].map((i) => (
              <Paper key={i} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Skeleton variant="text" width="50%" height={24} />
                  <Skeleton variant="circular" width={32} height={32} />
                </Box>
                <Skeleton variant="text" width="40%" height={20} />
                <Skeleton variant="text" width="60%" height={20} />
                <Skeleton variant="rectangular" width={90} height={24} sx={{ borderRadius: 1, mt: 1 }} />
              </Paper>
            ))}
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              {tableHead}
              <TableBody>
                {[1, 2, 3].map((i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton variant="text" width={140} /></TableCell>
                    <TableCell><Skeleton variant="text" width={70} /></TableCell>
                    <TableCell><Skeleton variant="text" width={100} /></TableCell>
                    <TableCell><Skeleton variant="text" width={120} /></TableCell>
                    <TableCell><Skeleton variant="text" width={80} /></TableCell>
                    <TableCell><Skeleton variant="rectangular" width={150} height={24} sx={{ borderRadius: 1 }} /></TableCell>
                    <TableCell><Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} /></TableCell>
                    <TableCell align="right"><Skeleton variant="circular" width={32} height={32} sx={{ ml: 'auto' }} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : targets.length === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography color="text.secondary">{t('admin.social.channels.empty')}</Typography>
        </Paper>
      ) : isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {targets.map((target) => (
            <Paper key={target.id} sx={{ p: 2, minWidth: 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                <Box sx={{ minWidth: 0 }}>{titleCell(target)}</Box>
                {actionsButton(target)}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                {t(`admin.social.platforms.${target.platform}`)} · {target.key} · {address(target)}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Typography component="span" variant="body2" color="text.secondary">
                  {t('admin.social.channels.columnToken')}:{' '}
                </Typography>
                {tokenCell(target)}
              </Box>
              <Box sx={{ mt: 1 }}>{lastCheckCell(target)}</Box>
              <Box sx={{ mt: 1 }}>{stateChip(target)}</Box>
            </Paper>
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            {tableHead}
            <TableBody>
              {targets.map((target) => (
                <TableRow key={target.id}>
                  <TableCell>{titleCell(target)}</TableCell>
                  <TableCell>{t(`admin.social.platforms.${target.platform}`)}</TableCell>
                  <TableCell>
                    <Box component="span" sx={{ fontFamily: 'monospace' }}>
                      {target.key}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ overflowWrap: 'anywhere' }}>{address(target)}</TableCell>
                  <TableCell>{tokenCell(target)}</TableCell>
                  <TableCell>{lastCheckCell(target)}</TableCell>
                  <TableCell>{stateChip(target)}</TableCell>
                  <TableCell align="right">{actionsButton(target)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        TransitionProps={{ onExited: () => setMenuTarget(null) }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            if (menuTarget) {
              setTokenTarget(menuTarget)
              setTokenOpen(true)
            }
            closeMenu()
          }}
        >
          {t('admin.social.channels.setToken')}
        </MenuItem>
        <MenuItem
          disabled={!menuTarget?.has_credentials}
          onClick={() => {
            if (menuTarget) void handleTest(menuTarget)
            closeMenu()
          }}
        >
          {t('admin.social.channels.test')}
        </MenuItem>
        {menuTarget?.enabled ? (
          <MenuItem
            onClick={() => {
              openConfirm('disable', menuTarget)
              closeMenu()
            }}
          >
            {t('admin.social.channels.disable')}
          </MenuItem>
        ) : (
          <MenuItem
            disabled={!menuTarget?.has_credentials}
            onClick={() => {
              if (menuTarget) void runAction(menuTarget, () => socialApi.updateTarget(menuTarget.id, { enabled: true }))
              closeMenu()
            }}
          >
            {t('admin.social.channels.enable')}
          </MenuItem>
        )}
        <MenuItem
          disabled={!menuTarget?.has_credentials}
          onClick={() => {
            if (menuTarget) openConfirm('revoke', menuTarget)
            closeMenu()
          }}
        >
          {t('admin.social.channels.revokeToken')}
        </MenuItem>
        <MenuItem
          disabled={Boolean(menuTarget?.enabled)}
          onClick={() => {
            if (menuTarget) {
              setEditTarget(menuTarget)
              setEditOpen(true)
            }
            closeMenu()
          }}
        >
          {t('admin.social.channels.edit')}
        </MenuItem>
      </Menu>

      <Dialog
        open={confirmOpen}
        onClose={() => {
          if (!confirming) setConfirmOpen(false)
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t(confirmKind === 'disable' ? 'admin.social.channels.disableTitle' : 'admin.social.channels.revokeTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ overflowWrap: 'anywhere' }}>
            {t(confirmKind === 'disable' ? 'admin.social.channels.disableText' : 'admin.social.channels.revokeText', {
              target: confirmTarget ? targetLabel(confirmTarget, confirmTarget.id) : '',
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={confirming}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirm}
            disabled={confirming || !confirmTarget}
            startIcon={confirming ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {t(confirmKind === 'disable' ? 'admin.social.channels.disable' : 'admin.social.channels.revokeToken')}
          </Button>
        </DialogActions>
      </Dialog>

      <SocialTargetCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(response) => {
          setCreateOpen(false)
          setError(null)
          setSuccess(response.message)
          void load()
        }}
      />

      <SocialTargetEditDialog
        open={editOpen}
        target={editTarget}
        onClose={() => setEditOpen(false)}
        onSaved={(response) => {
          setEditOpen(false)
          replaceTarget(response.data)
          setError(null)
          setSuccess(response.message)
        }}
      />

      <SocialTokenDialog
        open={tokenOpen}
        target={tokenTarget}
        onClose={() => setTokenOpen(false)}
        onSaved={(response) => {
          setTokenOpen(false)
          replaceTarget(response.data)
          setError(null)
          setSuccess(t('admin.social.channels.tokenSaved', { mask: response.data.credentials_mask ?? '' }))
        }}
      />
    </Box>
  )
}
